/**
 * Launch-capability accessors (ADR-0023).
 *
 * Pure, safe-defaulting readers for the `capabilities` map on a platform brand
 * (`PlatformCapabilitiesSchema` in `../schemas/platform-brand.ts`). Call sites
 * (route guards, nav, the pay path later) go through these instead of reaching
 * into `brand.capabilities.*` directly, so:
 *
 *   - a brand doc written before the field existed (or a partial map) still
 *     resolves to a defined boolean, and
 *   - the DEFAULT-PRESERVING contract lives in exactly one place: absence
 *     resolves to TRUE for both flags, i.e. enabling ADR-0023 changes nothing
 *     until an owner explicitly flips a flag off in the panel.
 */

/** The resolved, always-defined capability flags. */
export interface ResolvedPlatformCapabilities {
  /** Sales-agent recruiter layer (agent-pwa `/recruiter/*`, marketing `/become-a-recruiter`). */
  readonly recruiterChannel: boolean;
  /** Kilat-collected merchant-of-record wash payments (toyyibPay). */
  readonly marketplaceWashPayments: boolean;
  /** Weekly agent leaderboard (rollup maintained by `leaderboardOnSignup`, agent-pwa tile). */
  readonly leaderboard: boolean;
}

/**
 * ADR-0023 safe defaults. All TRUE so that turning the feature on is
 * behaviour-neutral: recruiter stays live, the already-live MOR pay path keeps
 * working, and the agent leaderboard ranks new signups. The owner opts *out*
 * per-flag via the super-admin panel.
 */
const CAPABILITY_DEFAULTS: ResolvedPlatformCapabilities = {
  recruiterChannel: true,
  marketplaceWashPayments: true,
  leaderboard: true,
};

/**
 * A structural, minimal view of the brand this module needs. Accepting this
 * instead of the full `PlatformBrand` keeps the helper decoupled from the
 * schema's output type and robust to `null` / `undefined` / partial maps.
 */
export interface BrandWithCapabilities {
  readonly capabilities?: Partial<ResolvedPlatformCapabilities> | null;
}

/** Resolve the full flag set for a brand, filling any missing flag with its safe default. */
export function brandCapabilities(
  brand: BrandWithCapabilities | null | undefined,
): ResolvedPlatformCapabilities {
  const c = brand?.capabilities;
  return {
    recruiterChannel: c?.recruiterChannel ?? CAPABILITY_DEFAULTS.recruiterChannel,
    marketplaceWashPayments:
      c?.marketplaceWashPayments ?? CAPABILITY_DEFAULTS.marketplaceWashPayments,
    leaderboard: c?.leaderboard ?? CAPABILITY_DEFAULTS.leaderboard,
  };
}

/** True when the recruiter channel is enabled (default true when the flag is absent). */
export function isRecruiterChannelEnabled(
  brand: BrandWithCapabilities | null | undefined,
): boolean {
  return brandCapabilities(brand).recruiterChannel;
}

/** True when Kilat-collected (MOR) wash payments are enabled (default true when absent). */
export function isMarketplaceWashPaymentsEnabled(
  brand: BrandWithCapabilities | null | undefined,
): boolean {
  return brandCapabilities(brand).marketplaceWashPayments;
}

/** True when the weekly agent leaderboard is enabled (default true when the flag is absent). */
export function isLeaderboardEnabled(brand: BrandWithCapabilities | null | undefined): boolean {
  return brandCapabilities(brand).leaderboard;
}
